import { Location } from "@angular/common";
import { Injectable, computed, inject, signal } from "@angular/core";
import { Navigation, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { isEmpty, minBy, sum } from "lodash/fp";
import { filter } from "rxjs/operators";

import { SitemapNode, sitemap } from "screens/app/constants/site-map";

const ENTRY_BOUNDARY_ROUTES = [
  "login",
  "forgot-password",
  "okta-callback",
  "okta-fallback",
  "client-auth",
];

@Injectable({
  providedIn: "root",
})
/**
 * Tracks the user's path through the site map.
 * Used by the navigation bar to display breadcrumbs.
 */
export class NavigationService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  /** User's session history with query parameters for each history entry. */
  private _history: { route: string; params: URLSearchParams }[] = [];

  private readonly _breadcrumbs = signal<SitemapNode[]>([]);
  public readonly breadcrumbs = this._breadcrumbs.asReadonly();

  private readonly backStackDepth = signal<number>(0);
  private backsAlreadyCounted = 0;

  public readonly canGoBack = computed<boolean>(() => this.backStackDepth() > 0);

  public constructor() {
    this._breadcrumbs.set(this.breadcrumbsFrom(window.location.pathname) ?? []);
    this._history.push({
      route: window.location.pathname,
      params: new URL(window.location.toString()).searchParams,
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const url = new URL(`${window.location.origin}${event.url}`);

        const breadcrumbs = this.breadcrumbsFrom(url.pathname) ?? [];
        this._breadcrumbs.set(breadcrumbs);
        if (breadcrumbs.length === 0)
          throw new Error(`A route is missing from the sitemap: ${url.pathname}`);
        // Only track history for non-root breadcrumbs
        if (breadcrumbs.length === 1 && isRootNode(breadcrumbs[0])) this._history = [];
        // Preserve parameters for routes
        else this._history.push({ route: url.pathname, params: url.searchParams });
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.trackBackStackDepth());
  }

  public back(): void {
    if (!this.canGoBack()) {
      return;
    }

    this.backStackDepth.update((depth) => depth - 1);
    this.backsAlreadyCounted += 1;
    this.location.back();
  }

  public queryParamsOf(node: SitemapNode): Record<string, string> {
    const params =
      this._history.find((entry) => entry.route.endsWith(node.route))?.params ??
      new URLSearchParams();
    return Object.fromEntries(params);
  }

  private trackBackStackDepth(): void {
    const navigation = this.router.lastSuccessfulNavigation();
    if (!navigation) {
      return;
    }

    if (navigation.trigger === "popstate") {
      if (this.backsAlreadyCounted > 0) {
        this.backsAlreadyCounted -= 1;
        return;
      }

      this.backStackDepth.update((depth) => Math.max(0, depth - 1));
      return;
    }

    const previous = navigation.previousNavigation;
    if (!previous || this.isEntryBoundary(previous) || this.isEntryBoundary(navigation)) {
      this.backStackDepth.set(0);
      this.backsAlreadyCounted = 0;
      return;
    }

    if (navigation.extras.replaceUrl || navigation.extras.skipLocationChange) {
      return;
    }

    this.backStackDepth.update((depth) => depth + 1);
  }

  private isEntryBoundary(navigation: Navigation): boolean {
    const url = this.router.serializeUrl(navigation.finalUrl ?? navigation.extractedUrl);
    const [path] = url.split(/[?#]/);
    const firstSegment = path.split("/").find((segment) => segment.length > 0);

    return firstSegment === undefined || ENTRY_BOUNDARY_ROUTES.includes(firstSegment);
  }

  private breadcrumbsFrom(path: string): SitemapNode[] | undefined {
    const routes = sitemap
      .map((node) => findBreadcrumbPath(path, node))
      .filter((x) => isEmpty(x) === false);
    if (routes.length === 1) return routes.at(0);

    // There are several candidates - need to select the best match
    const history = this._history.map((entry) => entry.route);
    const scores = routes.map((path) => rankMatchedBreadcrumbs(path, history));
    const maxScore = Math.max(...scores);
    const hasSingleBestMatch = scores.filter((score) => score === maxScore).length <= 1;
    if (hasSingleBestMatch) {
      const bestMatch = routes.at(scores.findIndex((score) => score === maxScore));
      if (bestMatch) return bestMatch;
    }

    // If there is no best match, try finding the default path
    const defaultRoute = routes.find((path) => path.at(-1).default);

    // if there is no path marked as default, fall back to the the shortest path
    return defaultRoute ?? minBy((path) => path.length, routes);
  }
}

/** Score a path through the sitemap based on how well it matches the user's nav history. */
function rankMatchedBreadcrumbs(path: SitemapNode[], history: string[]) {
  return path.reduce((matches, node) => {
    if (history.length)
      return matches + sum(history.map((entry) => entry.includes(node.route)));
    return matches;
  }, 0);
}

function isRootNode(node: SitemapNode) {
  return sitemap.includes(node);
}

/** Find a path through the sitemap given a URL's path. */
function findBreadcrumbPath(path: string, root: SitemapNode): SitemapNode[] {
  if (path.endsWith(root.route)) return [root];
  if (root.children === undefined) return [];
  const routes = root.children.map((node) => findBreadcrumbPath(path, node));
  const route = routes.find((route) => route.some((node) => path.endsWith(node.route)));
  return route ? [root].concat(route) : [];
}
